import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileDown, Loader2, Lock, Mail, Plus, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePdfExport } from '@/hooks/usePdfExport';
import { cn } from '@/lib/utils';

const API = API_BASE;

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
const Kpi = ({ n, l, tone }) => (
  <div className="rounded-xl bg-muted px-3 py-2"><div className={cn('text-xl font-bold leading-tight', tone)}>{n}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div></div>
);

/**
 * Daily production report (M3.5). Everything derived is shown live; the AD
 * types actual times, delays and a note. Send (email + PDF) is Studio.
 * Route: /workspaces/:workspaceId/films/:filmId/days/:scheduleId/dpr
 */
export default function DprPage() {
  const { workspaceId, filmId, scheduleId } = useParams();
  const { openUpgradeModal } = useWorkspace();
  const [film, setFilm] = useState(null);
  const [report, setReport] = useState(null);
  const [view, setView] = useState(null);
  const [canSend, setCanSend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { exportPdf, exporting } = usePdfExport();
  const timer = useRef(null);
  const canEdit = film?.can_edit !== false;
  const boardUrl = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}/board` : `/films/${filmId}/board`;
  const coverageUrl = workspaceId ? `/workspaces/${workspaceId}/films/${filmId}/days/${scheduleId}/coverage` : `/films/${filmId}/days/${scheduleId}/coverage`;

  const load = useCallback(async () => {
    try {
      const [f, d] = await Promise.all([
        axios.get(`${API}/films/${filmId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/films/${filmId}/schedules/${scheduleId}/dpr`, { headers: getAuthHeaders() }),
      ]);
      setFilm(f.data);
      setReport(d.data.report || {});
      setView(d.data.view);
      setCanSend(!!d.data.can_send);
      if (!emails && d.data.report?.sent_to?.length) setEmails(d.data.report.sent_to.map((s) => s.email).filter((v, i, a) => a.indexOf(v) === i).join(', '));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load the report');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filmId, scheduleId]);

  useEffect(() => { load(); }, [load]);

  const patch = (p) => {
    setReport((r) => ({ ...r, ...p }));
    if (!canEdit) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const { data } = await axios.put(`${API}/films/${filmId}/schedules/${scheduleId}/dpr`, p, { headers: getAuthHeaders() });
        setView(data.view);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not save');
      } finally {
        setSaving(false);
      }
    }, 600);
  };
  const setDelay = (i, k, v) => patch({ delays: (report.delays || []).map((d, j) => (j === i ? { ...d, [k]: v } : d)) });

  const send = async () => {
    const list = emails.split(/[,\s;]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one email'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/films/${filmId}/schedules/${scheduleId}/dpr/send`, { emails: list, message: message || null }, { headers: getAuthHeaders() });
      toast.success(`DPR sent to ${data.sent.length}${data.failed.length ? `, ${data.failed.length} failed` : ''}`, { description: data.pdf_attached ? 'PDF attached.' : undefined });
      setSendOpen(false);
      load();
    } catch (err) {
      if (err?.response?.status === 403) { setSendOpen(false); openUpgradeModal?.('dpr'); }
      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-muted/50 animate-pulse" />)}</div>;
  if (error || !view) return <div className="p-6 max-w-2xl mx-auto"><Link to={boardUrl} className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-4"><ArrowLeft className="size-3" /> Board</Link><div className="rounded-2xl border border-border p-8 text-center text-sm text-muted-foreground">{error}</div></div>;

  const t = view.totals;
  const tm = view.times;
  const studioGate = !canSend;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="dpr-page">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Link to={boardUrl} className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-1 hover:text-foreground"><ArrowLeft className="size-3" /> Board</Link>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">Daily production report</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {view.day_name}{view.day_number ? ` · Day ${view.day_number}${view.total_days ? ` of ${view.total_days}` : ''}` : ''} · {view.shoot_date_text || 'date to be confirmed'}
            {report?.status === 'sent' && <span className="text-green-600 dark:text-green-300"> · sent{report.sent_to?.length ? ` to ${report.sent_to.length}` : ''}</span>}
            {saving && <span className="inline-flex items-center gap-1 ml-2"><Loader2 className="size-3 animate-spin" /> saving</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="h-8" asChild><Link to={coverageUrl}>Coverage</Link></Button>
          <Button size="sm" variant="outline" className="h-8" disabled={exporting} data-testid="dpr-pdf"
            onClick={() => (studioGate ? openUpgradeModal?.('dpr') : exportPdf(`/films/${filmId}/schedules/${scheduleId}/dpr.pdf`, { kind: 'dpr', fallbackName: 'DPR.pdf' }))}>
            {studioGate ? <Lock className="size-4 mr-1" /> : <FileDown className="size-4 mr-1" />} PDF
          </Button>
          {canEdit && (
            <Button size="sm" className="h-8" data-testid="dpr-send" onClick={() => (studioGate ? openUpgradeModal?.('dpr') : setSendOpen(true))}>
              {studioGate ? <Lock className="size-4 mr-1" /> : <Send className="size-4 mr-1" />} Send to producer
            </Button>
          )}
        </div>
      </div>

      {studioGate && (
        <button type="button" onClick={() => openUpgradeModal?.('dpr')} className="w-full text-left rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5 text-xs flex items-center gap-2" data-testid="dpr-studio-hint">
          <Lock className="size-3.5 text-primary shrink-0" />
          <span><strong>Studio</strong> sends this report to your producer as a PDF every evening. You can read it here on any plan.</span>
          <span className="ml-auto font-semibold text-primary">Upgrade →</span>
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Kpi n={`${t.scenes_completed} / ${t.scenes_planned}`} l="Scenes completed" tone={t.scenes_planned && t.scenes_completed === t.scenes_planned ? 'text-green-600' : ''} />
        <Kpi n={`${t.shots_covered} / ${t.shots_planned}`} l={`Shots covered${t.coverage_pct != null ? ` · ${t.coverage_pct}%` : ''}`} />
        <Kpi n={t.pickups} l="Pickups owed" tone={t.pickups ? 'text-red-600' : ''} />
        <Kpi n={`${t.delay_minutes} min`} l="Delays" tone={t.delay_minutes ? 'text-red-600' : ''} />
        <Kpi n={tm.day_length || '—'} l="Day length" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-5">
        {/* Typed part */}
        <div className="space-y-4">
          <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Actual times</h2>
            <p className="text-xs text-muted-foreground">Planned call {tm.planned_call || '—'} · est. wrap {tm.planned_wrap || '—'}{tm.call_slip ? ` · call slipped ${tm.call_slip > 0 ? '+' : ''}${tm.call_slip}m` : ''}{tm.wrap_slip ? ` · wrap ${tm.wrap_slip > 0 ? '+' : ''}${tm.wrap_slip}m` : ''}</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {[['crew_call', 'Crew call'], ['first_shot', 'First shot'], ['lunch', 'Lunch'], ['last_shot', 'Last shot'], ['wrap', 'Wrap']].map(([k, l]) => (
                <Field key={k} label={l}><Input value={report?.[k] || ''} onChange={(e) => patch({ [k]: e.target.value })} placeholder="HH:MM" className="h-9 font-semibold" disabled={!canEdit} data-testid={`dpr-${k}`} /></Field>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-4 space-y-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Delays</h2>
            {(report?.delays || []).map((d, i) => (
              <div key={i} className="flex gap-2">
                <Input type="number" min="0" value={d.minutes ?? ''} onChange={(e) => setDelay(i, 'minutes', e.target.value)} placeholder="min" className="h-8 w-20 text-xs" disabled={!canEdit} />
                <Input value={d.reason || ''} onChange={(e) => setDelay(i, 'reason', e.target.value)} placeholder="Reason (rain, generator, actor late, permission…)" className="h-8 text-xs flex-1" disabled={!canEdit} />
                {canEdit && <button type="button" onClick={() => patch({ delays: report.delays.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><X className="size-4" /></button>}
              </div>
            ))}
            {canEdit && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => patch({ delays: [...(report?.delays || []), { minutes: 0, reason: '' }] })} data-testid="dpr-add-delay"><Plus className="size-3 mr-1" /> Add delay</Button>}
          </section>

          <section className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <Field label="Production notes / incidents"><Textarea value={report?.notes || ''} onChange={(e) => patch({ notes: e.target.value })} rows={3} disabled={!canEdit} placeholder="What the producer should know." data-testid="dpr-notes" /></Field>
            <Field label="Tomorrow (adds to the auto line)"><Textarea value={report?.next_day_notes || ''} onChange={(e) => patch({ next_day_notes: e.target.value })} rows={2} disabled={!canEdit} /></Field>
          </section>
        </div>

        {/* Derived part */}
        <div className="space-y-4">
          <section className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Scenes</div>
            <div className="divide-y divide-border">
              {view.scenes.map((sc) => (
                <div key={sc.number} className="px-3 py-2 flex items-start gap-3 text-sm">
                  <span className="font-bold w-8">{sc.number}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{sc.slug}</div>
                    <div className="text-xs text-muted-foreground">{sc.shots_covered}/{sc.shots_planned} covered{sc.shots_shot ? ` · ${sc.shots_shot} shot` : ''}{sc.pickups ? <span className="text-red-600"> · {sc.pickups} pickup</span> : ''}{sc.takes.length ? ` · ${sc.takes.join(' · ')}` : ''}</div>
                    {sc.notes.length > 0 && <div className="text-xs text-muted-foreground italic">{sc.notes.join(' · ')}</div>}
                  </div>
                  <span className={cn('text-[10px] font-semibold uppercase shrink-0', sc.status === 'completed' ? 'text-green-600' : sc.status === 'partial' ? 'text-amber-600' : 'text-muted-foreground')}>{sc.status}</span>
                </div>
              ))}
              {view.scenes.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">No scenes on this day.</p>}
            </div>
          </section>

          {view.pickups.length > 0 && (
            <section className="rounded-2xl border border-red-500/30 bg-red-500/5 overflow-hidden">
              <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">Pickups owed</div>
              <div className="divide-y divide-border">
                {view.pickups.map((p, i) => <div key={i} className="px-3 py-1.5 text-sm"><b>Sc {p.scene} · {p.shot_code}</b> {p.description}{p.note && <span className="text-xs text-muted-foreground"> — {p.note}</span>}</div>)}
              </div>
            </section>
          )}

          {view.cast_worked.length > 0 && <section className="rounded-2xl border border-border bg-card p-3 text-sm"><span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Cast worked</span>{view.cast_worked.join(', ')}</section>}

          {view.next_day && (
            <section className="rounded-2xl border border-border bg-card p-3 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tomorrow</div>
              <div className="font-semibold">{view.next_day.name}{view.next_day.date_text ? ` · ${view.next_day.date_text}` : ''}</div>
              <div className="text-xs text-muted-foreground">{view.next_day.locations.join(' / ')}{view.next_day.scenes.length ? ` · Sc ${view.next_day.scenes.join(', ')}` : ''}</div>
            </section>
          )}
        </div>
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="size-4 text-primary" /> Send the DPR</DialogTitle>
            <DialogDescription>Emails a summary with the full report attached as a PDF.</DialogDescription>
          </DialogHeader>
          <Field label="To (comma-separated)"><Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="producer@…, lineproducer@…" data-testid="dpr-emails" /></Field>
          <Field label="Message (optional)"><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={send} disabled={sending} data-testid="dpr-send-confirm">{sending ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Send className="size-4 mr-1" />} Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
